import React, { useState } from 'react';
import WarningAmber from '@mui/icons-material/WarningAmber';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import { useHistory, useLocation } from 'react-router-dom';
import { erpSettingsPathFromPathname } from 'pages/Layout/erpModulePaths';
import { useAppSelector } from 'redux/hooks';
import { selectUserEmail } from 'pages/app/redux';
import { selectPermission } from 'redux/global';
import { PERM } from 'pages/Settings/redux';
import { formatStorageBytes } from 'pages/Logs/logUtils';
import { useOrganizationStorage } from 'pages/hooks/useOrganizationStorage';
import { usePath } from 'pages/hooks';
import { isManagerReportsAlertActive } from './ManagerReportsAlert';
import { selectManagerReports } from 'pages/Reporting/redux';
import { useFormsInboxCounts } from 'pages/Forms/formsInboxCountsHook';
import LayoutAlert from './LayoutAlert';
import LayoutAlertPresence from './LayoutAlertPresence';

const DISMISS_STORAGE_KEY = 'storage_capacity_alert_dismissed_v1';
const DISMISS_STORAGE_KEY_LEGACY = 'logs_capacity_alert_dismissed_v2';
const DISMISS_MS = 7 * 24 * 60 * 60 * 1000;
const warnAccent = '#a67c00';
const criticalAccent = '#c62828';

type DismissState = { warnUntil: number; criticalUntil: number };

const emptyDismissState = (): DismissState => ({ warnUntil: 0, criticalUntil: 0 });

const readDismissState = (): DismissState => {
  try {
    const raw = localStorage.getItem(DISMISS_STORAGE_KEY)
      || localStorage.getItem(DISMISS_STORAGE_KEY_LEGACY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        warnUntil: Number(parsed?.warnUntil) || 0,
        criticalUntil: Number(parsed?.criticalUntil) || 0,
      };
    }
  } catch {
    /* ignore */
  }
  return emptyDismissState();
};

const StorageCapacityAlert = () => {
  const history = useHistory();
  const { pathname } = useLocation();
  const permission = useAppSelector(selectPermission);
  const userEmail = useAppSelector(selectUserEmail);
  const managerReports = useAppSelector(selectManagerReports);
  const { counts: formsInboxCounts } = useFormsInboxCounts(Boolean(userEmail));
  const { isHomePath } = usePath();
  const [dismissState, setDismissState] = useState(readDismissState);
  const { dbBytes, dbMaxBytes, othersBytes, othersMaxBytes, dbTone, othersTone, hasPlan, totalBytes, organizationMaxBytes } = useOrganizationStorage();

  // Report alerts outrank storage (MB) alerts.
  const managerReportsActive = isManagerReportsAlertActive(
    managerReports,
    isHomePath,
    userEmail,
    formsInboxCounts.openReportRequests,
  );

  const tone = (() => {
    if (dbTone === 'critical' || othersTone === 'critical') return 'critical';
    if (dbTone === 'warn' || othersTone === 'warn') return 'warn';
    return null;
  })();
  const alertKind = (() => {
    if (hasPlan) return 'storage';
    if (othersTone && (!dbTone || othersTone === 'critical')) return 'files';
    return 'database';
  })();

  const canSee = (permission || 0) >= PERM.SUB_MANAGER;
  const dismissedUntil = tone === 'critical'
    ? dismissState.criticalUntil
    : dismissState.warnUntil;
  const isDismissed = !!tone && Date.now() < dismissedUntil;

  const open = (
    !isHomePath
    && canSee
    && !managerReportsActive
    && !!tone
    && !isDismissed
  );

  const remaining = alertKind === 'files'
    ? Math.max(0, othersMaxBytes - othersBytes)
    : hasPlan
      ? Math.max(0, organizationMaxBytes - totalBytes)
      : Math.max(0, dbMaxBytes - dbBytes);
  const shownLimit = alertKind === 'files'
    ? othersMaxBytes
    : hasPlan
      ? organizationMaxBytes
      : dbMaxBytes;
  const isCritical = tone === 'critical';
  const accent = isCritical ? criticalAccent : warnAccent;

  const onGotIt = () => {
    const until = Date.now() + DISMISS_MS;
    const next: DismissState = {
      ...dismissState,
      ...(tone === 'critical' ? { criticalUntil: until } : { warnUntil: until }),
    };
    try {
      localStorage.setItem(DISMISS_STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
    setDismissState(next);
  };

  const storageKind = hasPlan
    ? 'storage'
    : alertKind === 'files'
      ? 'photo and file storage'
      : 'database storage';

  return (
    <LayoutAlertPresence open={open}>
      {tone ? (
        <LayoutAlert
          accent={accent}
          icon={isCritical
            ? <ErrorOutline fontSize="small" />
            : <WarningAmber fontSize="small" />}
          title={isCritical ? 'Storage full' : 'Storage running low'}
          text={isCritical
            ? (hasPlan
              ? `Your property has reached its ${formatStorageBytes(shownLimit)} ${storageKind} limit (seats 5GB plus any storage plan). New saves are blocked until you free space.`
              : `Your property has reached its ${formatStorageBytes(shownLimit)} ${storageKind} limit. New saves are blocked until you free space, or subscribe to user seats (5GB) and/or a storage plan.`)
            : (hasPlan
              ? `Your property has ${formatStorageBytes(remaining)} of ${formatStorageBytes(shownLimit)} ${storageKind} left (seats 5GB plus any storage plan). Free space soon to keep saving.`
              : `Your property has ${formatStorageBytes(remaining)} of ${formatStorageBytes(shownLimit)} ${storageKind} left. Subscribe to user seats (5GB) and/or a storage plan, or free space soon.`)}
          primaryVariant="outlined"
          secondaryAction={{ label: 'Got it', onClick: onGotIt }}
          primaryAction={{
            label: hasPlan ? 'Manage storage' : 'Get more storage',
            onClick: () => history.push(
              erpSettingsPathFromPathname(pathname, 'subscriptions', hasPlan ? 'storage' : 'user-seats'),
            ),
          }}
        />
      ) : null}
    </LayoutAlertPresence>
  );
};

export default StorageCapacityAlert;
